import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  LoadingState,
  PageHeader,
  useToast,
} from '@/components/ui';
import { InvoiceTable } from '@/features/billing/InvoiceTable';
import { formatMoney, formatPrice, statusLabel, statusTone } from '@/features/billing/money';
import { PlanCard } from '@/features/billing/PlanCard';
import { PlanLockedAction } from '@/features/billing/PlanLockedAction';
import { CHANGE, planChangeKind } from '@/features/billing/planChange';
import { PlanChangeDialog } from '@/features/billing/PlanChangeDialog';
import { UsageMeters } from '@/features/billing/UsageMeters';
import { InvoiceDownloadButton } from '@/features/studentBilling/InvoiceDownloadButton';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

/**
 * The company's plan, what it is using, and what it has been billed.
 *
 * Two things this screen is careful about. It never claims a change happened
 * immediately — the API says whether it did, and the message is repeated back
 * verbatim. And it confirms before a downgrade, because the thing being given
 * up is worth naming before it is given up.
 */
export function CompanyBillingPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(null);
  const [instructions, setInstructions] = useState(null);

  const subscription = useQuery({
    queryKey: ['company-subscription'],
    queryFn: companyApi.getSubscription,
  });
  const plans = useQuery({
    queryKey: ['company-plans'],
    queryFn: companyApi.listSubscriptionPlans,
  });
  const invoices = useQuery({
    queryKey: ['company-invoices'],
    queryFn: companyApi.listInvoices,
  });

  function refresh() {
    ['company-subscription', 'company-plans', 'company-invoices'].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    );
  }

  const change = useMutation({
    mutationFn: ({ planCode, couponCode }) => companyApi.changePlan(planCode, couponCode),
    onSuccess: (result) => {
      setConfirming(null);
      // Whatever the API says happened is what the customer is told. A screen
      // that assumes "upgraded!" would be wrong every time it was a downgrade.
      toast.success(result.message);
      setInstructions(result.payment_instructions ?? null);
      refresh();
    },
    onError: (error) => {
      setConfirming(null);
      toast.error(errorMessage(error));
    },
  });

  const resume = useMutation({
    mutationFn: companyApi.resumeSubscription,
    onSuccess: (result) => {
      toast.success(`You are staying on ${result.plan?.name ?? 'your current plan'}.`);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (subscription.isPending || plans.isPending) {
    return <LoadingState label="Loading your plan…" />;
  }
  if (subscription.isError) {
    return (
      <ErrorState
        message={errorMessage(subscription.error)}
        onRetry={() => void subscription.refetch()}
      />
    );
  }

  const current = subscription.data;
  const pending = current.cancel_at_period_end || current.scheduled_plan !== null;
  const periodEnd = current.current_period_end;
  const unpaid = current.pending_invoice;
  /** A paid plan whose current period has not been paid: nothing to wait out. */
  const unpaidPeriod = current.plan.code !== 'free' && current.period_paid === false;
  const paying = {
    code: current.plan.code,
    price_cents: current.plan.price_cents,
    currency: current.plan.currency,
    interval: current.plan.interval,
    running: current.plan.code !== 'free',
  };

  return (
    <div>
      <PageHeader
        title="Plan and billing"
        description="What you are on, what you are using, and what you have been charged."
      />

      {current.notice && (
        <Alert
          tone={current.status === 'PAST_DUE' ? 'warning' : 'info'}
          className="mb-6"
          title="About your plan"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span>{current.notice}</span>
            {pending && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => resume.mutate()}
                isLoading={resume.isPending}
              >
                Keep my current plan
              </Button>
            )}
          </div>
        </Alert>
      )}

      {unpaid && current.plan.code !== 'free' && (
        <Alert
          tone="info"
          className="mb-6"
          title={`Active — invoice ${unpaid.number} awaiting payment`}
        >
          {formatMoney(unpaid.amount_cents, unpaid.currency)} for {unpaid.description}, due{' '}
          {formatDate(unpaid.due_at)}. Your plan keeps running while the payment is on its way.
        </Alert>
      )}

      {instructions && (
        <Alert tone="info" className="mb-6" title="How to pay">
          {instructions}
        </Alert>
      )}

      <div className="mb-6 grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <Card>
          <CardHeader
            title="What you are using"
            description="Counted from your live listings and your workspace, not from a stored total."
          />
          <UsageMeters usage={current.usage} entitlements={current.plan.entitlements} />
        </Card>

        <Card className="border-brand-200 bg-brand-50/40">
          <CardHeader
            title="Your plan"
            action={
              <Badge tone="brand" dot>
                Current plan
              </Badge>
            }
          />
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-ink text-xl font-semibold">{current.plan.name}</p>
            <Badge dot tone={statusTone(current.status)}>
              {statusLabel(current.status)}
            </Badge>
          </div>
          {current.plan.price_cents != null && (
            <p className="text-ink mt-1 text-2xl font-semibold tracking-tight tabular-nums">
              {formatPrice(current.plan.price_cents, current.plan.currency, current.plan.interval)}
            </p>
          )}
          <dl className="border-brand-100 mt-4 space-y-2 border-t pt-4 text-sm">
            {current.current_period_end && (
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">
                  {current.cancel_at_period_end ? 'Ends' : 'Renews'}
                </dt>
                <dd className="text-ink">{formatDate(current.current_period_end)}</dd>
              </div>
            )}
            {current.outstanding_cents > 0 && (
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Unpaid</dt>
                <dd className="text-ink">
                  {formatMoney(current.outstanding_cents, current.plan.currency)}
                </dd>
              </div>
            )}
            {current.scheduled_plan && (
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Moving to</dt>
                <dd className="text-ink">{current.scheduled_plan.name}</dd>
              </div>
            )}
            {current.scheduled_plan && current.scheduled_coupon_code && (
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Code for its first bill</dt>
                <dd className="text-ink font-mono">{current.scheduled_coupon_code}</dd>
              </div>
            )}
          </dl>
          {paying.running &&
            (pending ? (
              <Button
                variant="secondary"
                size="sm"
                className="mt-4 w-full"
                onClick={() => resume.mutate()}
                isLoading={resume.isPending}
              >
                Keep my current plan
              </Button>
            ) : (
              <Button
                variant="ghostDanger"
                size="sm"
                className="mt-4 w-full"
                onClick={() => setConfirming({ code: 'free', name: 'Free' })}
              >
                Cancel my plan
              </Button>
            ))}
        </Card>
      </div>

      <h2 className="text-ink mb-4 text-base font-semibold tracking-tight">Plans</h2>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(plans.data ?? []).map((plan) => (
          <PlanCard
            key={plan.code}
            plan={plan}
            isBusy={change.isPending && change.variables?.planCode === plan.code}
            onChoose={(chosen) => setConfirming(chosen)}
            action={
              // The plan already chosen for later says so on its own card, so
              // the click visibly took effect wherever the page is scrolled.
              current.scheduled_plan?.code === plan.code ||
              (plan.code === 'free' && current.cancel_at_period_end) ? (
                <Button variant="secondary" className="w-full" disabled>
                  Scheduled from {formatDate(periodEnd)}
                </Button>
              ) : plan.locked_reason ? (
                <PlanLockedAction reason={plan.locked_reason} />
              ) : undefined
            }
          />
        ))}
      </div>

      <Card className="mt-6">
        <CardHeader title="Invoices" />
        {invoices.isPending ? (
          <LoadingState label="Loading invoices…" />
        ) : (
          <InvoiceTable
            invoices={invoices.data ?? []}
            action={(invoice) => (
              <InvoiceDownloadButton invoice={invoice} download={companyApi.downloadInvoicePdf} />
            )}
          />
        )}
      </Card>

      <ConfirmDialog
        open={confirming?.code === 'free'}
        title="Cancel your plan?"
        message={
          unpaidPeriod
            ? `${current.plan.name} has not been paid for yet, so it ends now and its unpaid invoice is cancelled. You move to Free straight away and can choose another plan; you keep every listing you have published.`
            : `Starts on ${formatDate(periodEnd)}, when your current period ends. Until then your plan runs as it is; after that you keep every listing you have published, but you will not be able to publish beyond the free allowance.`
        }
        confirmLabel="Confirm"
        isLoading={change.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => change.mutate({ planCode: 'free' })}
      />

      {/* A paid plan gets the dialog with the discount field. Cancelling does
          not: there is no bill for a code to come off, and the server refuses
          one there, so offering the box would be a promise it will not keep. */}
      <PlanChangeDialog
        plan={confirming?.code === 'free' ? null : confirming}
        open={confirming !== null && confirming.code !== 'free'}
        kind={
          confirming && confirming.code !== 'free' && !unpaidPeriod
            ? planChangeKind(confirming, paying)
            : // An unpaid period has nothing to wait out: the change starts now.
              CHANGE.UPGRADE
        }
        replacesUnpaid={unpaidPeriod}
        currentName={current.plan.name}
        periodEnd={periodEnd}
        isLoading={change.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={(planCode, couponCode) => change.mutate({ planCode, couponCode })}
      />
    </div>
  );
}
